import { observer } from 'mobx-react-lite';
import { createContext, useContext, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

interface TabMenuContextType<T> {
    selected: T;
    onChange: (value: T) => void;
}

const TabMenuContext = createContext<TabMenuContextType<any> | null>(null);

const TabMenuItem = observer(
    <T,>({
        value,
        children,
        className = '',
    }: {
        value: T;
        children: ReactNode;
        className?: string;
    }) => {
        const context = useContext(TabMenuContext);
        if (!context) throw new Error('TabMenu.Item must be used within a TabMenu');

        const { selected, onChange } = context;
        const isActive = selected === value;

        return (
            <a
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    if (!isActive) {
                        onChange(value);
                    }
                }}
                className={twMerge('tab', isActive && 'tab-active font-semibold', className)}
            >
                {children}
            </a>
        );
    },
);

const TabMenu = Object.assign(
    observer(
        <T,>({
            className = '',
            selected,
            onChange,
            children,
        }: {
            className?: string;
            selected: T;
            onChange: (value: T) => void;
            children: ReactNode;
        }) => {
            return (
                <TabMenuContext.Provider value={{ selected, onChange }}>
                    <div className={twMerge('tabs tabs-box shadow-none', className)}>
                        {children}
                    </div>
                </TabMenuContext.Provider>
            );
        },
    ),
    {
        Item: TabMenuItem,
    },
);

export default TabMenu;
